import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container } from "@/components/layout/Section";
import { NAV_LINKS, SERVICE_CATEGORIES, SITE } from "@/config/site";
import { cn } from "@/lib/utils";

const ServicesDropdown = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const closeOnFocusOut = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={closeOnFocusOut}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="nav-services-dropdown-button"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        className={cn("nav-link inline-flex items-center gap-1", open && "text-brand-snow")}
      >
        Services
        <ChevronDown size={14} className={cn("transition-transform duration-200", open && "rotate-180")} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 top-full z-50 mt-3 w-[34rem] -translate-x-1/2 rounded-xl border border-brand-mist/15 bg-brand-charcoal/95 p-3 shadow-card backdrop-blur-xl"
            data-testid="nav-services-dropdown-panel"
          >
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {SERVICE_CATEGORIES.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/services/${cat.slug}`}
                  data-testid={`nav-category-${cat.slug}`}
                  onClick={() => setOpen(false)}
                  className="group rounded-lg p-3 transition-colors duration-200 hover:bg-white/5"
                >
                  <span className="flex items-center justify-between font-display text-sm font-semibold text-brand-snow">
                    {cat.name}
                    <ArrowUpRight
                      size={14}
                      className="text-brand-mist opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    />
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-brand-mist">{cat.blurb}</span>
                </Link>
              ))}
              <Link
                to="/services"
                data-testid="nav-category-all-services"
                onClick={() => setOpen(false)}
                className="group rounded-lg border border-brand-emerald/20 bg-brand-emerald/5 p-3 transition-colors duration-200 hover:bg-brand-emerald/10"
              >
                <span className="flex items-center justify-between font-display text-sm font-semibold text-brand-emerald">
                  View all services
                  <ArrowUpRight size={14} />
                </span>
                <span className="mt-1 block text-xs text-brand-mist">The complete A2Z capability stack.</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ open, onClose }) => {
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-40 flex flex-col bg-brand-black/95 pt-24 backdrop-blur-md lg:hidden"
          data-testid="mobile-menu-panel"
        >
          <Container className="flex flex-1 flex-col gap-1 overflow-y-auto pb-10">
            <button
              type="button"
              aria-expanded={servicesOpen}
              data-testid="mobile-services-toggle"
              onClick={() => setServicesOpen((v) => !v)}
              className="flex items-center justify-between border-b border-brand-mist/10 py-4 text-left font-display text-xl font-semibold text-brand-snow"
            >
              Services
              <ChevronDown size={18} className={cn("transition-transform", servicesOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {SERVICE_CATEGORIES.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/services/${cat.slug}`}
                      data-testid={`mobile-category-${cat.slug}`}
                      onClick={onClose}
                      className="block border-b border-brand-mist/5 py-3 pl-4 text-sm text-brand-mist transition-colors hover:text-brand-emerald"
                    >
                      {cat.name}
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    data-testid="mobile-category-all"
                    onClick={onClose}
                    className="block border-b border-brand-mist/5 py-3 pl-4 text-sm font-medium text-brand-emerald"
                  >
                    View all services
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-${link.testid}`}
                onClick={onClose}
                className="border-b border-brand-mist/10 py-4 font-display text-xl font-semibold text-brand-snow transition-colors hover:text-brand-emerald"
              >
                {link.label}
              </Link>
            ))}
            <Link to="/admin/login" data-testid="mobile-login-link" onClick={onClose} className="mt-6 block border-b border-brand-mist/10 py-4 font-display text-xl font-semibold text-brand-mist transition-colors hover:text-brand-emerald">
              Login
            </Link>
            <div className="mt-8 space-y-4">
              <SiteButton to="/contact" testid="mobile-cta-consultation" className="w-full" onClick={onClose}>
                Book a consultation
              </SiteButton>
              <div className="space-y-1 text-sm text-brand-mist">
                <a href={`mailto:${SITE.email}`} data-testid="mobile-email-link" className="block hover:text-brand-emerald">
                  {SITE.email}
                </a>
                <a href={SITE.phoneHref} data-testid="mobile-phone-link" className="block hover:text-brand-emerald">
                  {SITE.phone}
                </a>
              </div>
            </div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300 ease-brand",
          scrolled ? "glass py-3" : "bg-transparent py-5"
        )}
      >
        <Container className="flex items-center justify-between">
          <Link to="/" aria-label="A2Z Rankings home" data-testid="header-logo-link">
            <Logo />
          </Link>
          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
            <ServicesDropdown />
            {NAV_LINKS.map((link) => (
              <Link key={link.href} to={link.href} data-testid={link.testid} className="nav-link">
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="hidden items-center gap-4 lg:flex">
            <Link to="/admin/login" data-testid="header-login-link" className="nav-link text-sm text-brand-mist transition-colors hover:text-brand-emerald">
              Login
            </Link>
            <SiteButton to="/contact" size="sm" testid="header-cta-consultation">
              Book a consultation
            </SiteButton>
          </div>
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-md p-2 text-brand-snow transition-colors hover:text-brand-emerald lg:hidden"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </Container>
      </header>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
};

export default Header;
